/**
 * File save that works on iOS Safari.
 *
 * iOS ignores `<a download>` and opens images, video, and PDF in the tab.
 * When the file is already a Blob (or small enough to fetch), iOS gets the
 * system share sheet so the user can choose Save to Files. Other browsers
 * keep the normal download link.
 */

/** Skip the in-memory path for cuts that would exhaust a phone tab. */
const IOS_MAX_BYTES = 48 * 1024 * 1024;

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/** Save a blob. On iOS this opens the share sheet; elsewhere it downloads. */
export async function saveDownloadBlob(blob: Blob, filename: string): Promise<void> {
  const name = filename || "download";
  if (isIos() && (await shareBlob(blob, name))) return;
  const url = URL.createObjectURL(blob);
  clickAnchor(url, name, isIos());
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * Save a remote or blob URL.
 * Desktop uses the download attribute. iOS fetches the file and shares it
 * when it is small enough; otherwise it opens a new tab, same as before.
 */
export async function saveDownloadUrl(
  href: string,
  filename: string,
  newTab = false,
): Promise<void> {
  const name = filename || "download";
  if (!href) return;
  if (isIos() && (await shareUrl(href, name))) return;
  clickAnchor(href, name, newTab || isIos());
}

async function shareUrl(href: string, filename: string): Promise<boolean> {
  try {
    const resp = await fetch(href, { mode: "cors", credentials: "omit" });
    if (!resp.ok) return false;
    const len = Number(resp.headers.get("content-length") || "0");
    if (len > IOS_MAX_BYTES) return false;
    const blob = await resp.blob();
    if (blob.size > IOS_MAX_BYTES) return false;
    return shareBlob(blob, filename);
  } catch {
    return false;
  }
}

async function shareBlob(blob: Blob, filename: string): Promise<boolean> {
  const type = blob.type || "application/octet-stream";
  const file = new File([blob], filename, { type });
  if (!navigator.share || !navigator.canShare?.({ files: [file] })) return false;
  try {
    await navigator.share({ files: [file], title: filename });
    return true;
  } catch (err) {
    // The user closed the sheet. Do not also open the file.
    return (err as DOMException)?.name === "AbortError";
  }
}

function clickAnchor(href: string, filename: string, newTab: boolean): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.rel = "noopener noreferrer";
  if (newTab) a.target = "_blank";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 1000);
}
